'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError, toClientErrorReport } from '@tourtask/utils/client/reportClientError';

type Props = { children: ReactNode };
type State = { hasError: boolean };

/**
 * App-wide React error boundary whose job is to capture the React COMPONENT
 * STACK — which Next.js's file-convention `error.tsx`/`global-error.tsx` do NOT
 * expose (they receive only `{ error, reset }`) — and beacon it to
 * `/api/client-error`. That turns a minified, anonymous browser throw into a
 * report that names the exact component that failed.
 *
 * Mount it high (around the provider tree) so it catches both provider- and
 * page-level render throws. It is a transparent pass-through when nothing
 * throws — `render` returns `children` unchanged — so it has no effect on the
 * happy path. The fallback is self-contained (plain markup + inline styles, no
 * providers/theme/translations, since it may be catching a failure in those)
 * and offers a reload, which also recovers the common stale-chunk case.
 */
export class ClientErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    reportClientError(
      toClientErrorReport(error, {
        kind: 'render',
        componentStack: errorInfo.componentStack ?? undefined,
      }),
    );
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          boxSizing: 'border-box',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 12px' }}>Something went wrong</h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#555', margin: '0 0 24px' }}>
            This page hit an unexpected error. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 22px',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
